import { type Movie, MovieStatus, ScreenType, type Tv, TvStatus } from '@tmdb-graphql-api/schema/types';
import { type TileItem } from '#components/Tile/types.ts';
import { WatchlistCategory } from '#components/Watchlist/WatchlistCategory.ts';
import { formatMovieReleaseDate } from '#components/Watchlist/helpers/formatMovieReleaseDate.ts';
import { formatTvAirDates } from '#components/Watchlist/helpers/formatTvAirDates.ts';
import { type Watchlist } from '#components/Watchlist/types.ts';
import { ImageSize, PosterSize } from '#enums/imageSizes.ts';
import { buildImagePath } from '#helpers/buildImagePath.ts';
import { formatMovie, formatTv } from '#helpers/formatMoviesAndTvResults.ts';
import { tt } from '#i18n/index.ts';
import { type Config } from '#types.ts';

export type WatchlistTileItem = {
  airDate: string | undefined;
  description?: string;
  id: string | number;
  item: TileItem;
  metadata?: (string | number)[];
  screenType: ScreenType;
  src: string;
  status: MovieStatus | TvStatus;
  tagline?: string;
  title: string;
  type: ScreenType;
  voteAverage?: number;
};

const isMovieWatchlistItem = (
  item: WatchlistTileItem,
): item is Omit<WatchlistTileItem, 'status'> & { status: MovieStatus } => item.screenType === ScreenType.Movie;

const isTvWatchlistItem = (item: WatchlistTileItem): item is Omit<WatchlistTileItem, 'status'> & { status: TvStatus } =>
  item.screenType === ScreenType.Tv;

const formatWatchlistMovie = (movie: Movie, config: Config): WatchlistTileItem => {
  const metadata = [];

  if (movie.releaseDate) {
    metadata.push(formatMovieReleaseDate(movie.releaseDate, movie.status));
  } else {
    metadata.push(tt('watchlist.movieReleaseDateFallback'));
  }

  return {
    airDate: movie.releaseDate ?? undefined,
    description: movie.overview,
    id: movie.id,
    item: formatMovie(movie, config),
    metadata,
    screenType: ScreenType.Movie,
    src: buildImagePath(movie.posterPath, ImageSize.Poster, PosterSize.SM, config.images),
    status: movie.status,
    tagline: movie.tagline ?? undefined,
    title: movie.title,
    type: ScreenType.Movie,
    voteAverage: movie.voteAverage,
  };
};

const formatWatchlistTv = (tv: Tv, config: Config): WatchlistTileItem => {
  const metadata = [];

  if (tv.firstAirDate) {
    metadata.push(formatTvAirDates(tv.firstAirDate, tv.lastAirDate, tv.nextEpisodeToAir?.airDate, tv.status));
  } else {
    metadata.push(tt('watchlist.tvAirDateFallback'));
  }

  return {
    airDate: tv.nextEpisodeToAir?.airDate ?? undefined,
    description: tv.overview,
    id: tv.id,
    item: formatTv(tv, config),
    metadata,
    screenType: ScreenType.Tv,
    src: buildImagePath(tv.posterPath, ImageSize.Poster, PosterSize.SM, config.images),
    status: tv.status,
    tagline: tv.tagline ?? undefined,
    title: tv.name,
    type: ScreenType.Tv,
    voteAverage: tv.voteAverage,
  };
};

const sortWatchlistItems = (watchlistItems: WatchlistTileItem[]): Watchlist => {
  const sortedWatchlistItems = watchlistItems.sort((a, b) => {
    const { airDate: airDateA } = a;
    const { airDate: airDateB } = b;

    if (!airDateA && airDateB) {
      return 1;
    } else if (airDateA && !airDateB) {
      return -1;
    } else if (!airDateA && !airDateB) {
      return 0;
    }

    // At this point, both air dates are defined
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const dateA = new Date(airDateA!);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const dateB = new Date(airDateB!);

    if (dateA < dateB) {
      return 1;
    } else if (dateA > dateB) {
      return -1;
    } else {
      return 0;
    }
  });

  const watchlist: Watchlist = {
    totalWatchlistItems: 0,
    watchlistItems: {
      [WatchlistCategory.Canceled]: [],
      [WatchlistCategory.Completed]: [],
      [WatchlistCategory.InProduction]: [],
      [WatchlistCategory.Planned]: [],
      [WatchlistCategory.Upcoming]: [],
    },
  };

  for (const item of sortedWatchlistItems) {
    switch (true) {
      case ((isMovieWatchlistItem(item) || isTvWatchlistItem(item)) && item.status === MovieStatus.Canceled) ||
        item.status === TvStatus.Canceled: {
        watchlist.watchlistItems[WatchlistCategory.Canceled].push(item);
        watchlist.totalWatchlistItems += 1;
        break;
      }

      case (isMovieWatchlistItem(item) && item.status === MovieStatus.Released) ||
        (isTvWatchlistItem(item) && item.status === TvStatus.Ended): {
        watchlist.watchlistItems[WatchlistCategory.Completed].push(item);
        watchlist.totalWatchlistItems += 1;
        break;
      }

      case (isMovieWatchlistItem(item) || isTvWatchlistItem(item)) &&
        (item.status === MovieStatus.InProduction || item.status === TvStatus.InProduction): {
        watchlist.watchlistItems[WatchlistCategory.InProduction].push(item);
        watchlist.totalWatchlistItems += 1;
        break;
      }

      case (isMovieWatchlistItem(item) || isTvWatchlistItem(item)) &&
        (item.status === MovieStatus.Rumored ||
          item.status === MovieStatus.Planned ||
          item.status === TvStatus.Planned): {
        watchlist.watchlistItems[WatchlistCategory.Planned].push(item);
        watchlist.totalWatchlistItems += 1;
        break;
      }

      case (isMovieWatchlistItem(item) || isTvWatchlistItem(item)) &&
        (item.status === MovieStatus.PostProduction ||
          item.status === TvStatus.Pilot ||
          item.status === TvStatus.ReturningSeries): {
        watchlist.watchlistItems[WatchlistCategory.Upcoming].push(item);
        watchlist.totalWatchlistItems += 1;
        break;
      }
    }
  }

  return watchlist;
};

export const formatWatchlist = (
  movies: Movie[] | undefined = [],
  tv: Tv[] | undefined = [],
  config: Config,
): Watchlist => {
  const formatted: WatchlistTileItem[] = [
    ...movies.map(entry => formatWatchlistMovie(entry, config)),
    ...tv.map(entry => formatWatchlistTv(entry, config)),
  ];

  return sortWatchlistItems(formatted);
};
